import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout';
import ApiClient from '../../utils/ApiClient';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import envConfig from '../../utils/EnvironmentConfigs';
import { formatRequestId } from '../../utils/RequestIdHelper';
import { firstResolvedDisplayName, sanitizeDisplayName } from '../../utils/DisplayValueUtils';

const REVIEW_DECISION_DRAFT = 'DRAFT';
const REVIEW_DECISION_APPROVED = 'APPROVED';
const DEFAULT_REQUIRED_REVIEW_TYPE = 'SUPERVISOR';

const normalizeTimestamp = (value) => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    const rawValue = String(value).trim();
    if (!rawValue) {
        return null;
    }

    const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(rawValue);
    const normalizedValue = hasTimezone ? rawValue : `${rawValue}Z`;
    const parsed = new Date(normalizedValue);
    if (!Number.isNaN(parsed.getTime())) {
        return parsed;
    }

    const fallback = new Date(rawValue);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
};

const extractRequestList = (responseData) =>
    Array.isArray(responseData) ? responseData : responseData?.content ?? responseData?.requests ?? [];

const getRequestId = (request) =>
    request?.id ?? request?.requestId ?? request?.uuid ?? request?.accessRequestId ?? null;

const normalizeTypeLabel = (value) =>
    String(value ?? '')
        .toLowerCase()
        .split('_')
        .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1)}` : part))
        .join(' ');

const addDays = (date, days) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null;
    }
    const copy = new Date(date.getTime());
    copy.setUTCDate(copy.getUTCDate() + days);
    return copy;
};

const daysUntil = (targetDate, nowDate) => {
    if (!targetDate || !nowDate) {
        return null;
    }
    const diffMs = targetDate.getTime() - nowDate.getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

const formatDateTime = (value) => {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
        return 'Not available';
    }

    return value.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
};

const getCurrentCycle = (request, reviewData) => {
    const normalizedReviews = (Array.isArray(reviewData) ? reviewData : [])
        .map((review, index) => {
            const createdAt = normalizeTimestamp(review?.createdAt ?? review?.updatedAt);
            return {
                ...review,
                _type: String(review?.type ?? '').trim().toUpperCase(),
                _decision: String(review?.decision ?? '').trim().toUpperCase(),
                _createdAt: createdAt,
                _index: index,
            };
        })
        .filter((review) => review._type && review._decision && review._createdAt)
        .sort((left, right) => {
            const leftTime = left._createdAt.getTime();
            const rightTime = right._createdAt.getTime();
            if (leftTime !== rightTime) {
                return leftTime - rightTime;
            }
            return left._index - right._index;
        });

    const requiredTypes = new Set(
        normalizedReviews
            .filter((review) => review._decision === REVIEW_DECISION_DRAFT)
            .map((review) => review._type)
    );

    if (requiredTypes.size === 0) {
        requiredTypes.add(DEFAULT_REQUIRED_REVIEW_TYPE);
    }

    const findCycleApprovalCompletionAfter = (cycleStartMs) => {
        const firstApprovalByType = new Map();

        normalizedReviews.forEach((review) => {
            if (!requiredTypes.has(review._type) || review._decision !== REVIEW_DECISION_APPROVED) {
                return;
            }

            const reviewTime = review._createdAt.getTime();
            if (cycleStartMs !== null && reviewTime < cycleStartMs) {
                return;
            }

            if (!firstApprovalByType.has(review._type)) {
                firstApprovalByType.set(review._type, reviewTime);
            }
        });

        const allTypesCovered = Array.from(requiredTypes).every((type) => firstApprovalByType.has(type));
        if (!allTypesCovered) {
            return null;
        }

        return Math.max(...firstApprovalByType.values());
    };

    const findDraftCycleStartAfter = (thresholdMs) => {
        let draftStart = null;

        normalizedReviews.forEach((review) => {
            if (!requiredTypes.has(review._type) || review._decision !== REVIEW_DECISION_DRAFT) {
                return;
            }

            const reviewTime = review._createdAt.getTime();
            if (reviewTime <= thresholdMs) {
                return;
            }

            if (draftStart === null || reviewTime < draftStart) {
                draftStart = reviewTime;
            }
        });

        return draftStart;
    };

    const approvedCycleCompletion = findCycleApprovalCompletionAfter(null);
    const requestUpdatedAt = normalizeTimestamp(request?.updatedAt);
    const requestCreatedAt = normalizeTimestamp(request?.createdAt);
    let certifiedAtMs =
        approvedCycleCompletion ??
        requestUpdatedAt?.getTime() ??
        requestCreatedAt?.getTime() ??
        Date.now();

    while (true) {
        const pendingCycleStartedAt = findDraftCycleStartAfter(certifiedAtMs);

        if (pendingCycleStartedAt === null) {
            return {
                certifiedAt: new Date(certifiedAtMs),
                pendingCycleStartedAt: null,
                pendingReviewTypes: [],
            };
        }

        const cycleCompletedAt = findCycleApprovalCompletionAfter(pendingCycleStartedAt);
        if (cycleCompletedAt === null) {
            const latestCycleReviewByType = new Map();

            normalizedReviews.forEach((review) => {
                const reviewTime = review._createdAt.getTime();
                if (!requiredTypes.has(review._type) || reviewTime < pendingCycleStartedAt) {
                    return;
                }
                latestCycleReviewByType.set(review._type, review);
            });

            const pendingReviewTypes = Array.from(latestCycleReviewByType.entries())
                .filter(([, review]) => review?._decision === REVIEW_DECISION_DRAFT)
                .map(([type]) => type);

            return {
                certifiedAt: new Date(certifiedAtMs),
                pendingCycleStartedAt: new Date(pendingCycleStartedAt),
                pendingReviewTypes,
            };
        }

        certifiedAtMs = cycleCompletedAt;
    }
};

const RecertificationsPage = ({ authDetails, onSignOut }) => {
    const [implementedRequests, setImplementedRequests] = useState([]);
    const [reviewsByRequestId, setReviewsByRequestId] = useState(new Map());
    const [groupLookup, setGroupLookup] = useState(new Map());
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const requesterUuid = String(
        authDetails?.identity?.tokenParsed?.sub ?? authDetails?.identity?.subject ?? ''
    ).trim();
    const recertificationDays = envConfig.recertificationDays;
    const recertificationRemovalDays = envConfig.recertificationRemovalDays;
    const dueMilestonePercent = Math.max(
        0,
        Math.min(100, (recertificationDays / recertificationRemovalDays) * 100)
    );

    useEffect(() => {
        let isMounted = true;
        const apiUrl = envConfig.apiBaseUrl;

        if (!apiUrl) {
            setImplementedRequests([]);
            setReviewsByRequestId(new Map());
            setGroupLookup(new Map());
            setLoadError('API URL is not configured.');
            setIsLoading(false);
            return () => {
                isMounted = false;
            };
        }

        if (!requesterUuid) {
            setImplementedRequests([]);
            setReviewsByRequestId(new Map());
            setGroupLookup(new Map());
            setLoadError('Your session is missing a user identifier.');
            setIsLoading(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchRecertifications = async () => {
            setIsLoading(true);
            setLoadError('');
            try {
                const requestsResponse = await ApiClient.get('/access-requests', {
                    params: {
                        page: 0,
                        size: 100,
                        requester: requesterUuid,
                        state: 'IMPLEMENTED',
                    },
                });

                if (requestsResponse.status !== 200) {
                    throw new Error(`API responded with ${requestsResponse.status}`);
                }

                const requestList = extractRequestList(requestsResponse.data ?? {});
                const nextRequests = Array.isArray(requestList) ? requestList : [];

                const reviewEntries = await Promise.all(
                    nextRequests.map(async (request) => {
                        const requestId = getRequestId(request);
                        if (!requestId) {
                            return null;
                        }

                        try {
                            const reviewsResponse = await ApiClient.get(
                                `/access-requests/${encodeURIComponent(requestId)}/review`
                            );
                            return [
                                String(requestId),
                                Array.isArray(reviewsResponse?.data) ? reviewsResponse.data : [],
                            ];
                        } catch (error) {
                            return [String(requestId), []];
                        }
                    })
                );

                if (!isMounted) {
                    return;
                }

                const nextReviewLookup = new Map();
                reviewEntries.filter(Boolean).forEach(([requestId, reviews]) => {
                    nextReviewLookup.set(requestId, reviews);
                });

                setImplementedRequests(nextRequests);
                setReviewsByRequestId(nextReviewLookup);
            } catch (error) {
                if (isMounted) {
                    setImplementedRequests([]);
                    setReviewsByRequestId(new Map());
                    setGroupLookup(new Map());
                    setLoadError('We could not load your recertifications.');
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchRecertifications();

        return () => {
            isMounted = false;
        };
    }, [requesterUuid]);

    const recertificationItems = useMemo(() => {
        const now = new Date();

        return implementedRequests
            .map((request) => {
                const requestId = getRequestId(request);
                if (!requestId) {
                    return null;
                }

                const reviews = reviewsByRequestId.get(String(requestId)) ?? [];
                const cycle = getCurrentCycle(request, reviews);
                if (!cycle.pendingCycleStartedAt || cycle.pendingReviewTypes.length === 0) {
                    return null;
                }

                const recertificationDueAt = addDays(cycle.certifiedAt, recertificationDays);
                const revocationAt = addDays(cycle.certifiedAt, recertificationRemovalDays);
                const elapsedDays = Math.max(
                    0,
                    Math.floor((now.getTime() - cycle.certifiedAt.getTime()) / (1000 * 60 * 60 * 24))
                );
                const progressPercent = Math.max(
                    0,
                    Math.min(100, (elapsedDays / recertificationRemovalDays) * 100)
                );
                const groupId = String(request?.groupUuid ?? request?.groupId ?? '').trim();
                const roles = Array.isArray(request?.roles)
                    ? request.roles
                        .map((role) =>
                            String(
                                role?.name ??
                                    role?.displayName ??
                                    role?.id ??
                                    role?.uuid ??
                                    role ??
                                    ''
                            ).trim()
                        )
                        .filter(Boolean)
                    : request?.role
                      ? [String(request.role).trim()]
                      : [];
                const daysUntilRevocation = daysUntil(revocationAt, now);
                const daysUntilDue = daysUntil(recertificationDueAt, now);
                const statusText =
                    daysUntilRevocation !== null && daysUntilRevocation < 0
                        ? 'Revocation deadline reached.'
                        : daysUntilDue !== null && daysUntilDue < 0
                          ? `${daysUntilRevocation ?? 0} day(s) remaining before revocation.`
                          : `${daysUntilDue ?? 0} day(s) until recertification is due.`;

                return {
                    id: String(requestId),
                    displayId: formatRequestId(requestId),
                    groupId,
                    roles,
                    pendingReviewTypes: cycle.pendingReviewTypes,
                    certifiedAt: cycle.certifiedAt,
                    recertificationDueAt,
                    revocationAt,
                    progressPercent,
                    statusText,
                };
            })
            .filter(Boolean);
    }, [implementedRequests, recertificationDays, recertificationRemovalDays, reviewsByRequestId]);

    const activeGroupIds = useMemo(
        () => Array.from(new Set(recertificationItems.map((item) => item.groupId).filter(Boolean))),
        [recertificationItems]
    );

    useEffect(() => {
        let isMounted = true;

        if (activeGroupIds.length === 0) {
            setGroupLookup(new Map());
            return () => {
                isMounted = false;
            };
        }

        const fetchGroups = async () => {
            const groups = await Promise.all(
                activeGroupIds.map(async (groupId) => {
                    try {
                        const response = await ApiClient.get(`/groups/${encodeURIComponent(groupId)}`);
                        return {
                            id: groupId,
                            name: firstResolvedDisplayName(
                                [response?.data?.name, response?.data?.displayName],
                                'Unknown group'
                            ),
                        };
                    } catch (error) {
                        return {
                            id: groupId,
                            name: 'Unknown group',
                        };
                    }
                })
            );

            if (!isMounted) {
                return;
            }

            const nextLookup = new Map();
            groups.forEach((group) => {
                if (group?.id) {
                    nextLookup.set(group.id, sanitizeDisplayName(group.name, 'Unknown group'));
                }
            });
            setGroupLookup(nextLookup);
        };

        fetchGroups();

        return () => {
            isMounted = false;
        };
    }, [activeGroupIds]);

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    <div className="usa-prose">
                        <h1>Recertifications</h1>
                        <p>
                            Review your currently granted groups and roles that are in active recertification.
                            Access enters recertification at day {recertificationDays} and is revoked at day{' '}
                            {recertificationRemovalDays} if approvals are not completed.
                        </p>

                        {isLoading ? (
                            <div className="recertification-list" aria-hidden="true">
                                {Array.from({ length: 2 }).map((_, index) => (
                                    <article key={`recertification-loading-${index}`} className="usa-card recertification-card">
                                        <div className="usa-card__container">
                                            <div className="usa-card__header">
                                                <h2 className="usa-card__heading">
                                                    <ValueWithSkeleton loading width="10ch" />
                                                </h2>
                                            </div>
                                            <div className="usa-card__body">
                                                <p className="recertification-card__status">
                                                    <ValueWithSkeleton loading width="12ch" />
                                                </p>
                                                <p>
                                                    <strong>Group requiring recertification:</strong>{' '}
                                                    <ValueWithSkeleton loading type="sentence" width="18ch" />
                                                </p>
                                                <p>
                                                    <strong>Roles requiring recertification:</strong>{' '}
                                                    <ValueWithSkeleton loading type="sentence" width="24ch" />
                                                </p>
                                                <p>
                                                    <strong>Pending review(s):</strong>{' '}
                                                    <ValueWithSkeleton loading type="sentence" width="20ch" />
                                                </p>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        ) : null}

                        {!isLoading && loadError ? (
                            <div className="usa-alert usa-alert--error margin-top-2" role="alert">
                                <div className="usa-alert__body">
                                    <p className="usa-alert__text">{loadError}</p>
                                </div>
                            </div>
                        ) : null}

                        {!isLoading && !loadError && recertificationItems.length === 0 ? (
                            <p>No active recertification workflows were found for your current access.</p>
                        ) : null}

                        {!isLoading && !loadError && recertificationItems.length > 0 ? (
                            <div className="recertification-list">
                                {recertificationItems.map((item) => (
                                    <article key={item.id} className="usa-card recertification-card">
                                        <div className="usa-card__container">
                                            <div className="usa-card__header">
                                                <h2 className="usa-card__heading">{item.displayId}</h2>
                                            </div>
                                            <div className="usa-card__body">
                                                <p className="recertification-card__status">{item.statusText}</p>
                                                <p>
                                                    <strong>Group requiring recertification:</strong>{' '}
                                                    {item.groupId
                                                        ? groupLookup.get(item.groupId) ?? 'Unknown group'
                                                        : 'None listed'}
                                                </p>
                                                <p>
                                                    <strong>Roles requiring recertification:</strong>{' '}
                                                    {item.roles.length > 0 ? item.roles.join(', ') : 'None listed'}
                                                </p>
                                                <p>
                                                    <strong>Pending review(s):</strong>{' '}
                                                    {item.pendingReviewTypes.map(normalizeTypeLabel).join(', ')}
                                                </p>

                                                <div
                                                    className="recertification-timeline"
                                                    aria-label={`Recertification timeline for ${item.displayId}`}
                                                >
                                                    <div className="recertification-timeline__track">
                                                        <div
                                                            className="recertification-timeline__progress"
                                                            style={{ width: `${item.progressPercent}%` }}
                                                        />
                                                        <span
                                                            className="recertification-timeline__milestone recertification-timeline__milestone--due"
                                                            style={{
                                                                left: `${dueMilestonePercent}%`,
                                                            }}
                                                        />
                                                        <span className="recertification-timeline__milestone recertification-timeline__milestone--revocation" />
                                                    </div>
                                                    <div className="recertification-timeline__labels">
                                                        <p>
                                                            <strong>Day 0:</strong> Last certified{' '}
                                                            {formatDateTime(item.certifiedAt)}
                                                        </p>
                                                        <p>
                                                            <strong>Day {recertificationDays}:</strong> Recertification due{' '}
                                                            {formatDateTime(item.recertificationDueAt)}
                                                        </p>
                                                        <p>
                                                            <strong>Day {recertificationRemovalDays}:</strong> Access revoked if
                                                            not recertified by {formatDateTime(item.revocationAt)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        ) : null}

                        <Link to="/home" className="usa-button usa-button--outline">
                            Back to home
                        </Link>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default RecertificationsPage;
